import { athletes } from './mockData';

// Livelli di performance per ogni test
export type PerformanceLevel = 'poor' | 'average' | 'good' | 'elite';

// Interfaccia per i threshold di un test
export interface TestThreshold {
  elite: number;    // Valore eccezionale
  good: number;     // Valore goal
  average: number;  // Valore minimo accettabile
  poor: number;     // Sotto questo = poor (per higherIsBetter sarà -Infinity, per lowerIsBetter sarà Infinity)
}

// Funzione per ottenere peso e altezza dell'atleta (dai risultati body composition)
function getAthleteStats(athleteId: string): { weight: number; height: number } {
  // Valori di default realistici per atleti U16-U18
  return {
    weight: 70, // kg
    height: 175, // cm
  };
}

// Definizione thresholds per ogni test - DINAMICI basati su atleta
export function getTestThresholds(testId: string, athleteId: string): TestThreshold {
  const stats = getAthleteStats(athleteId);
  const bodyWeight = stats.weight;
  
  // Dizionario con tutti i threshold
  const thresholds: Record<string, TestThreshold> = {
    // ============================================
    // JUMP (Salto)
    // ============================================
    'test-cmj': {
      elite: 50,
      good: 42,
      average: 35,
      poor: -Infinity
    },
    'test-dropjump': {
      elite: 52,
      good: 44,
      average: 37,
      poor: -Infinity
    },
    'test-dropjump-rsi': {
      elite: 2.5,
      good: 2.0,
      average: 1.5,
      poor: -Infinity
    },
    
    // ============================================
    // STRENGTH (Forza)
    // ============================================
    'test-1rm-squat': {
      elite: bodyWeight * 2.0,
      good: bodyWeight * 1.75,
      average: bodyWeight * 1.5,
      poor: -Infinity
    },
    'test-1rm-bench': {
      elite: bodyWeight * 1.3,
      good: bodyWeight * 1.1,
      average: bodyWeight * 0.9,
      poor: -Infinity
    },
    'test-1rm-deadlift': {
      elite: bodyWeight * 2.3,
      good: bodyWeight * 2.0,
      average: bodyWeight * 1.7,
      poor: -Infinity
    },
    'test-1rm-clean': {
      elite: bodyWeight * 1.2,
      good: bodyWeight * 1.0,
      average: bodyWeight * 0.8,
      poor: -Infinity
    },
    'test-1rm-snatch': {
      elite: bodyWeight * 1.0,
      good: bodyWeight * 0.85,
      average: bodyWeight * 0.7,
      poor: -Infinity
    },
    'test-pullup': {
      elite: 15,
      good: 12,
      average: 8,
      poor: -Infinity
    },
    'test-pushup': {
      elite: 50,
      good: 40,
      average: 30,
      poor: -Infinity
    },
    'test-plank': {
      elite: 120,
      good: 90,
      average: 60,
      poor: -Infinity
    },
    'test-legpress': {
      elite: bodyWeight * 3.5,
      good: bodyWeight * 3.0,
      average: bodyWeight * 2.5,
      poor: -Infinity
    },
    'test-handgrip': {
      elite: 55,
      good: 48,
      average: 40,
      poor: -Infinity
    },
    
    // ============================================
    // SPEED (Velocità) - lower is better
    // ============================================
    'test-10m': {
      elite: 1.65,
      good: 1.75,
      average: 1.85,
      poor: Infinity
    },
    'test-20m': {
      elite: 2.95,
      good: 3.10,
      average: 3.25,
      poor: Infinity
    },
    'test-30m': {
      elite: 4.00,
      good: 4.20,
      average: 4.40,
      poor: Infinity
    },
    'test-40m': {
      elite: 5.10,
      good: 5.35,
      average: 5.60,
      poor: Infinity
    },
    'test-5-10-5': {
      elite: 4.50,
      good: 4.75,
      average: 5.00,
      poor: Infinity
    },
    'test-t-test': {
      elite: 9.00,
      good: 9.50,
      average: 10.00,
      poor: Infinity
    },
    'test-illinois': {
      elite: 15.00,
      good: 15.80,
      average: 16.50,
      poor: Infinity
    },
    'test-flying10': {
      elite: 1.00,
      good: 1.10,
      average: 1.20,
      poor: Infinity
    },
    'test-505': {
      elite: 2.20,
      good: 2.35,
      average: 2.50,
      poor: Infinity
    },
    
    // ============================================
    // ENDURANCE (Resistenza)
    // ============================================
    'test-yoyo': {
      elite: 2200,
      good: 1900,
      average: 1600,
      poor: -Infinity
    },
    'test-vo2max': {
      elite: 58,
      good: 53,
      average: 48,
      poor: -Infinity
    },
    'test-cooper': {
      elite: 2900,
      good: 2600,
      average: 2300,
      poor: -Infinity
    },
    'test-beep': {
      elite: 13.0,
      good: 11.5,
      average: 10.0,
      poor: -Infinity
    },
    'test-1500m': {
      elite: 300,  // 5:00 min
      good: 330,   // 5:30 min
      average: 360, // 6:00 min
      poor: Infinity
    },
    'test-3000m': {
      elite: 660,  // 11:00 min
      good: 720,   // 12:00 min
      average: 780, // 13:00 min
      poor: Infinity
    },
    'test-5000m': {
      elite: 1140, // 19:00 min
      good: 1260,  // 21:00 min
      average: 1380, // 23:00 min
      poor: Infinity
    },
    'test-rast': {
      elite: 700,
      good: 600,
      average: 500,
      poor: -Infinity
    },
    
    // ============================================
    // BODY COMPOSITION (Composizione)
    // ============================================
    'test-weight': {
      // Per peso, i threshold sono relativi al range ideale
      // Usiamo un approccio neutro: il range "good" è vicino al peso forma
      elite: 72,    // Peso ideale ottimale
      good: 75,     // Range accettabile alto
      average: 78,  // Limite superiore
      poor: Infinity // Sopra questo
    },
    'test-height': {
      // Height non ha threshold di performance, è una misura statica
      elite: 180,
      good: 175,
      average: 170,
      poor: -Infinity
    },
    'test-bodyfat': {
      elite: 10,    // <10% = elite per atleti
      good: 13,     // <13% = good
      average: 16,  // <16% = average
      poor: Infinity
    },
    'test-leanmass': {
      elite: 65,
      good: 60,
      average: 55,
      poor: -Infinity
    },
    'test-bmi': {
      // BMI ideale per atleti: 20-23
      elite: 22,
      good: 23.5,
      average: 25,
      poor: Infinity
    },
    'test-waist': {
      elite: 75,
      good: 80,
      average: 85,
      poor: Infinity
    },
    'test-skinfold': {
      elite: 40,
      good: 55,
      average: 70,
      poor: Infinity
    },
  };
  
  return thresholds[testId] || {
    elite: 0,
    good: 0,
    average: 0,
    poor: 0
  };
}

// Funzione per determinare il livello di performance di un valore
export function getPerformanceLevel(value: number, testId: string, athleteId: string, higherIsBetter: boolean): PerformanceLevel {
  const thresholds = getTestThresholds(testId, athleteId);
  
  if (higherIsBetter) {
    // Per test dove più alto = meglio
    if (value >= thresholds.elite) return 'elite';
    if (value >= thresholds.good) return 'good';
    if (value >= thresholds.average) return 'average';
    return 'poor';
  } else {
    // Per test dove più basso = meglio (es. sprint, body fat)
    if (value <= thresholds.elite) return 'elite';
    if (value <= thresholds.good) return 'good';
    if (value <= thresholds.average) return 'average';
    return 'poor';
  }
}

// Funzione per ottenere il colore di un livello
export function getPerformanceLevelColor(level: PerformanceLevel): string {
  const colors: Record<PerformanceLevel, string> = {
    elite: '#42A5F5',    // Blu
    good: '#66BB6A',     // Verde chiaro
    average: '#FFA726',  // Arancione
    poor: '#f57c73',     // Rosso
  };
  return colors[level];
}

// Funzione per ottenere la label in italiano di un livello
export function getPerformanceLevelLabel(level: PerformanceLevel): string {
  const labels: Record<PerformanceLevel, string> = {
    elite: 'Eccezionale',
    good: 'Goal',
    average: 'Minimi',
    poor: 'Sotto i minimi',
  };
  return labels[level];
}

// Funzione per ottenere la sigla breve di un livello (per badge compatti)
export function getPerformanceLevelShort(level: PerformanceLevel): string {
  const shorts: Record<PerformanceLevel, string> = {
    elite: 'E',
    good: 'G',
    average: 'M',
    poor: 'S',
  };
  return shorts[level];
}
